import React, { memo, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@campgladiator/cgui-core.atoms.button'
import { Card } from '@campgladiator/cgui-core.atoms.card'
import { FormPart } from '@campgladiator/cgui-core.molecules.form-part'
import { Heading } from '@campgladiator/cgui-core.atoms.typography'
import { Input } from '@campgladiator/cgui-core.atoms.input'
import { PinOrPasswordInput } from '../PinOrPasswordInput'
import {
  emailValidationRegex,
  phoneValidationRegex,
} from '../../../../common/utility/regex'
import { useAppDispatch } from '../../../../redux/store'
import { login, resetError } from '../../../../redux/features/Auth'
import { LoginPanelProps, SecretType } from '../../Login.d'
import styles from './LoginPanel.module.scss'

const pinLength = 4

const validatePhone = (text: string): boolean => phoneValidationRegex.test(text)
const validateEmail = (text: string): boolean =>
  emailValidationRegex.test(text) && !text.trim().match(' ')

const credentialsAsObject = (type: SecretType, id: string, pass: string) =>
  type === 'password' ? { email: id, password: pass } : { phone: id, pin: pass }

const LoginPanel = ({
  className = '',
  isLoginPending,
  authError,
  ...props
}: LoginPanelProps) => {
  const [identifier, setIdentifier] = useState('')
  const [isEmailOrPhoneSubmitted, setIsEmailOrPhoneSubmitted] = useState(false)
  const [typeOfSecret, setTypeOfSecret] = useState<SecretType>('')
  const [secret, setSecret] = useState('')
  const dispatch = useAppDispatch()

  const sanitizePhoneNumber = (phone: string) => phone.replace(/\D/g, '')
  const sanitizeEmailAddress = (email: string) => email.trim().toLowerCase()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isEmailOrPhoneSubmitted) {
      const typeOfSecret = validatePhone(identifier) ? 'pin' : 'password'
      const sanitizedIdentifier =
        typeOfSecret === 'pin'
          ? sanitizePhoneNumber(identifier)
          : sanitizeEmailAddress(identifier)
      setIsEmailOrPhoneSubmitted(true)
      setIdentifier(sanitizedIdentifier)
      setTypeOfSecret(typeOfSecret)
    } else {
      dispatch(login(credentialsAsObject(typeOfSecret, identifier, secret)))
    }
  }

  const handleSecretChange = (text: string) => {
    setSecret(text)
    authError && dispatch(resetError())
  }

  const handleIdentifierChange = (text: string) => {
    setIdentifier(text)
    if (isEmailOrPhoneSubmitted) {
      setIsEmailOrPhoneSubmitted(false)
      setTypeOfSecret('')
      handleSecretChange('')
    }
  }

  return (
    <Card raised className={classNames(styles.content, className)} {...props}>
      <Heading type="h2" className={styles.heading}>
        Log In
      </Heading>
      <form className={styles.form} onSubmit={handleSubmit}>
        <FormPart
          forId="identifier"
          label={identifier ? 'Email Address or Phone Number' : ''}
        >
          <Input
            name="identifier"
            id="identifier"
            placeholder="Email Address or Phone Number"
            invalid={!!authError}
            value={identifier}
            onChange={(e) => handleIdentifierChange(e.target.value)}
          />
        </FormPart>
        {isEmailOrPhoneSubmitted && (
          <PinOrPasswordInput
            invalid={!!authError}
            errorMsg={authError}
            inputType={typeOfSecret}
            valueChangeHandler={handleSecretChange}
            pinLength={pinLength}
          />
        )}
        {!isEmailOrPhoneSubmitted ? (
          <Button
            className={styles.button}
            disabled={!(validatePhone(identifier) || validateEmail(identifier))}
          >
            Next
          </Button>
        ) : (
          <Button
            className={styles.button}
            disabled={
              !!authError ||
              isLoginPending ||
              (typeOfSecret === 'password'
                ? secret.length < 1
                : secret.length !== pinLength)
            }
          >
            Log In
          </Button>
        )}
      </form>
    </Card>
  )
}

export default memo(LoginPanel)
